/**
 * ARCHX AI — imperative Three.js scene manager for the in-portal case viewer.
 *
 * Plain three (no react-three-fiber): React owns the UI/layer panel; this class
 * owns the WebGL scene. Layers are arbitrary Object3D (Mesh or Group); colour /
 * opacity / visibility are driven per layer from the panel.
 */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default class SceneManager {
  constructor(container) {
    this.container = container;
    this.layers = new Map(); // id -> Object3D
    this.dataLayers = new Set(); // ids whose per-mesh materials must be kept

    const w = container.clientWidth || 800;
    const h = container.clientHeight || 600;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0e1116);

    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 10000);
    this.camera.position.set(0, 0, 200);

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(this.renderer.domElement);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;

    this.scene.add(new THREE.AmbientLight(0xffffff, 0.65));
    const key = new THREE.DirectionalLight(0xffffff, 0.85);
    key.position.set(1, 1, 1);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.4);
    fill.position.set(-1, -0.5, -1);
    this.scene.add(fill);

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);

    this._raf = null;
    this._loop = this._loop.bind(this);
    this._loop();
  }

  _loop() {
    this._raf = requestAnimationFrame(this._loop);
    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }

  resize() {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    if (!w || !h) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  /**
   * Add an Object3D as a layer; assigns a standard material to every mesh in it.
   * The object is wrapped in a pivot Group centred on its bounding-box centre,
   * so manual rotation (setLayerTransform) happens about the layer's centroid.
   */
  addLayer(id, object3d, color, opacity = 1, opts = {}) {
    if (opts.keepMaterials) {
      this.dataLayers.add(id);                 // per-mesh colors (e.g. deviation)
      object3d.traverse((o) => {
        if (o.isMesh && o.material) {
          o.material.transparent = opacity < 1;
          o.material.opacity = opacity;
        }
      });
    } else {
      object3d.traverse((o) => {
        if (o.isMesh) {
          o.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(color),
            metalness: 0.1,
            roughness: 0.8,
            transparent: opacity < 1,
            opacity,
            side: THREE.DoubleSide,
          });
        }
      });
    }
    const box = new THREE.Box3().setFromObject(object3d);
    const center = box.getCenter(new THREE.Vector3());
    object3d.position.sub(center);          // recenter contents at local origin
    const pivot = new THREE.Group();
    pivot.position.copy(center);            // net world position unchanged
    pivot.userData.baseCenter = center.clone();
    pivot.add(object3d);
    this.scene.add(pivot);
    this.layers.set(id, pivot);
  }

  /** Manual alignment: translate (mm) + rotate (degrees, XYZ) about the centroid. */
  setLayerTransform(id, t = [0, 0, 0], rDeg = [0, 0, 0]) {
    const pivot = this.layers.get(id);
    if (!pivot) return;
    const base = pivot.userData.baseCenter || new THREE.Vector3();
    pivot.position.set(base.x + t[0], base.y + t[1], base.z + t[2]);
    pivot.rotation.set(
      THREE.MathUtils.degToRad(rDeg[0]),
      THREE.MathUtils.degToRad(rDeg[1]),
      THREE.MathUtils.degToRad(rDeg[2]),
    );
  }

  /** Current world-space centroid of a layer (after any manual transform). */
  getCentroid(id) {
    const pivot = this.layers.get(id);
    if (!pivot) return null;
    return new THREE.Box3().setFromObject(pivot).getCenter(new THREE.Vector3());
  }

  setVisible(id, visible) {
    const o = this.layers.get(id);
    if (o) o.visible = visible;
  }

  setColor(id, color) {
    if (this.dataLayers.has(id)) return;        // keep per-mesh data colors
    const o = this.layers.get(id);
    if (!o) return;
    o.traverse((m) => { if (m.isMesh) m.material.color.set(color); });
  }

  setOpacity(id, opacity) {
    const o = this.layers.get(id);
    if (!o) return;
    o.traverse((m) => {
      if (m.isMesh) {
        m.material.opacity = opacity;
        m.material.transparent = opacity < 1;
        m.material.needsUpdate = true;
      }
    });
  }

  removeLayer(id) {
    const o = this.layers.get(id);
    if (!o) return;
    this.scene.remove(o);
    o.traverse((m) => {
      if (m.isMesh) {
        m.geometry?.dispose();
        m.material?.dispose();
      }
    });
    this.layers.delete(id);
    this.dataLayers.delete(id);
  }

  /** Frame the camera to the union bounding box of all visible layers. */
  fitToScene() {
    const box = new THREE.Box3();
    this.layers.forEach((o) => { if (o.visible) box.expandByObject(o); });
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const dist = (maxDim / (2 * Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2)))) * 1.6;
    this.controls.target.copy(center);
    this.camera.position.copy(center).add(new THREE.Vector3(0, 0, dist));
    this.camera.near = Math.max(0.1, dist / 100);
    this.camera.far = dist * 100;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  dispose() {
    if (this._raf) cancelAnimationFrame(this._raf);
    window.removeEventListener('resize', this._onResize);
    Array.from(this.layers.keys()).forEach((id) => this.removeLayer(id));
    this.controls.dispose();
    this.renderer.dispose();
    const el = this.renderer.domElement;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }
}
